import { useMutation } from '@tanstack/react-query';
import saveAs from 'save-as';

import getExportReportHostSummary from 'Api/report/getExportReportHostSummary';
import getExportReportBrand from 'Api/report/getExportReportBrand';
import getExportReportSession from 'Api/report/getExportReportSession';
import getExportReportAccountPlatform from 'Api/report/getExportReportAccountPlatform';
import { useModal } from 'Store/modal';
import { useLoader } from 'Store/loader';
import { TextRegular } from 'UiKit/typography';
import { reportExportText } from 'Const/report/text';

import { useReportState } from 'View/report/state';

import getReportHostSummaryRequest from './model/request/getReportHostSummaryRequest';
import getReportBrandRequest from './model/request/getReportBrandRequest';
import getReportSessionRequest from './model/request/getReportSessionRequest';
import getReportAccountPlatformRequest from './model/request/getReportAccountPlatformRequest';

const useGetExportReport = () => {
  const { state, updateState, filterState } = useReportState();
  const { onShowLoader, onResetLoader } = useLoader();
  const { onOpenModal, onResetModal } = useModal();

  return useMutation({
    mutationFn: async (onRefetchExportReport) => {
      let queries;
      let response;

      onResetModal();
      updateState({ errorProps: {} });

      switch (state?.selectedTab) {
        case 'host':
          queries = getReportHostSummaryRequest(filterState);

          response = await getExportReportHostSummary(queries);

          break;

        case 'brand':
          queries = getReportBrandRequest(filterState);

          response = await getExportReportBrand(queries);

          break;

        case 'session':
          queries = getReportSessionRequest(filterState);

          response = await getExportReportSession(queries);

          break;

        case 'accountPlatform':
          queries = getReportAccountPlatformRequest(filterState);

          response = await getExportReportAccountPlatform(queries);

          break;

        default:
          break;
      }

      return { onRefetchExportReport, response };
    },
    onMutate: () => {
      onShowLoader({ variant: 'loader-primary' });
    },

    onSuccess: ({ response, onRefetchExportReport }) => {
      let filename = `report-achievement-${state?.selectedTab}.xlsx`;

      const blob = new Blob([response]);
      saveAs(blob, filename);

      onOpenModal({
        type: 'success',
        isOpen: true,
        contentTitle: reportExportText?.modalContentTitle,
        otherContent: (
          <TextRegular mb="64px">
            {reportExportText?.modalOtherContentText}
            <span
              onClick={() => onRefetchExportReport(onRefetchExportReport)}
              style={{ cursor: 'pointer', color: 'var(--color-info70)', fontWeight: 'bold', borderBottom: '1px solid var(--color-info70)', marginLeft: '4px' }}
            >
              {reportExportText?.modalOtherContentLinkText}
            </span>
          </TextRegular>
        ),
        showPrimaryButton: false,
        showSecondaryButton: false,
      });
    },
    onError: () => {
      updateState({
        errorProps: {
          isOpen: true,
          type: 'error',
          title: reportExportText?.errorTitle,
          description: reportExportText?.errorDescription,
          onClose: () => updateState({ errorProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default useGetExportReport;
